import * as THREE from 'three';

/**
 * CAMERA CONTROLLER // TECHNICAL PERSPECTIVE & ORTHOGRAPHIC PLAN VIEW
 * Manages low-FOV camera trajectory, section choreography, and damped mouse parallax.
 */

export function createCameraController(fov = 28) {
  const aspect = window.innerWidth / window.innerHeight;
  const camera = new THREE.PerspectiveCamera(fov, aspect, 0.1, 1000);

  // Default camera target & current state
  const targetLookAt = new THREE.Vector3(0, 0, 0);
  const currentLookAt = new THREE.Vector3(0, 0, 0);

  // Parallax angles
  const mouse = { x: 0, y: 0, targetX: 0, targetY: 0 };
  const maxParallax = (4 * Math.PI) / 180; // Max 4 degrees

  // Waypoints for scroll choreography
  const cameraStates = {
    hero: {
      position: new THREE.Vector3(-2.8, 2.8, 14.0),
      lookAt: new THREE.Vector3(-1.4, 0.1, 0)
    },
    about: {
      position: new THREE.Vector3(0.5, 2.2, 9.8),
      lookAt: new THREE.Vector3(0, 0.2, 0)
    },
    systems: {
      position: new THREE.Vector3(1.8, 2.8, 10.5),
      lookAt: new THREE.Vector3(0.3, 0.4, 0)
    },
    projects: {
      position: new THREE.Vector3(0, 5.0, 15.0),
      lookAt: new THREE.Vector3(0, 0, 0)
    },
    experience: {
      // Top-down orthographic CAD plan view
      position: new THREE.Vector3(0, 16.0, 0.01),
      lookAt: new THREE.Vector3(0, 0, 0)
    },
    contact: {
      position: new THREE.Vector3(0, 1.8, 9.2),
      lookAt: new THREE.Vector3(0, -1.8, 0)
    }
  };

  // Setup initial hero pose
  camera.position.copy(cameraStates.hero.position);
  targetLookAt.copy(cameraStates.hero.lookAt);
  currentLookAt.copy(cameraStates.hero.lookAt);

  // Mouse move listener for parallax
  if (window.matchMedia('(pointer: fine)').matches) {
    window.addEventListener('mousemove', e => {
      mouse.targetX = (e.clientX / window.innerWidth) * 2 - 1;
      mouse.targetY = -(e.clientY / window.innerHeight) * 2 + 1;
    }, { passive: true });
  }

  function updateParallax(delta) {
    mouse.x += (mouse.targetX - mouse.x) * 0.05;
    mouse.y += (mouse.targetY - mouse.y) * 0.05;

    camera.rotation.y += mouse.x * maxParallax * 0.02;
    camera.rotation.x += mouse.y * maxParallax * 0.02;
  }

  function handleResize() {
    const width = window.innerWidth;
    const height = window.innerHeight;
    camera.aspect = width / height;

    // Adjust hero offset on smaller screens
    if (width < 768) {
      cameraStates.hero.position.set(0, 4.5, 18.0);
      cameraStates.hero.lookAt.set(0, 0, 0);
    } else {
      cameraStates.hero.position.set(2.5, 3.8, 14.5);
      cameraStates.hero.lookAt.set(1.2, 0.2, 0);
    }

    camera.updateProjectionMatrix();
  }

  window.addEventListener('resize', handleResize);
  handleResize();

  return {
    camera,
    cameraStates,
    targetLookAt,
    currentLookAt,
    updateParallax
  };
}
